import * as PIXI from "pixi.js";
import {Animatable} from "./Animatable";
import {Callback, Mix} from "./Reflection";
import {Scalable} from "./Scalable";
import {ISignal, Signal} from "./Signal";

export interface ISpineTrack {
    animate(animationName: string, duration?: number | Callback, cb?: Callback): PIXI.spine.core.TrackEntry;
    getCurrentTrack(): PIXI.spine.core.TrackEntry;
    stopAnimation(mix?: number): void;
}

export class Spine extends Mix(PIXI.spine.Spine, Scalable, Animatable) implements ISpineTrack {

    get onAnimationComplete(): ISignal<(track: PIXI.spine.core.TrackEntry) => void> {
        this.addSpineListeners();
        return this._onAnimationComplete;
    }

    get onAnimationEvent(): ISignal<(track: PIXI.spine.core.TrackEntry, event: PIXI.spine.core.Event) => void> {
        this.addSpineListeners();
        return this._onAnimationEvent;
    }

    private _onAnimationComplete!: ISignal<(track: PIXI.spine.core.TrackEntry) => void>;
    private _onAnimationEvent!: ISignal<(track: PIXI.spine.core.TrackEntry, event: PIXI.spine.core.Event) => void>;

    private readonly _tracks: SpineTrack[] = [];

    constructor(skeletonData: PIXI.spine.core.SkeletonData) {
        super(skeletonData);

        this.autoUpdate = false;
    }

    public animate(animationName: string, duration?: number | Callback, cb?: Callback): PIXI.spine.core.TrackEntry {
        return this.track(0).animate(animationName, duration, cb);
    }

    public getCurrentTrack(): PIXI.spine.core.TrackEntry {
        return this.state.getCurrent(0);
    }

    public stopAnimation(mix: number = 0): void {
        this.track(0).stopAnimation(mix);
    }

    public track(index: number): ISpineTrack {
        if (!this._tracks[index]) {
            this._tracks[index] = new SpineTrack(this, index);
        }
        return this._tracks[index];
    }

    public setMixes(mix: number, from?: string | RegExp, to?: string | RegExp): void {
        const fromString = typeof from === "string" ? from : null;
        const toString = typeof to === "string" ? to : null;
        if (fromString && toString) {
            this.stateData.setMix(fromString, toString, mix);
        } else {
            const fromRegExp = from instanceof RegExp ? from : null;
            const toRegExp = to instanceof RegExp ? to : null;
            const isFrom = fromString || fromRegExp;
            const isTo = toString || toRegExp;
            const testFrom = (name: string) => {
                return (fromString && name === fromString) || (fromRegExp && fromRegExp.test(name));
            };
            const testTo = (name: string) => {
                return (toString && name === toString) || (toRegExp && toRegExp.test(name));
            };
            const names = this.spineData.animations.map((it) => it.name);
            for (const fromName of names) {
                for (const toName of names) {
                    if (fromName !== toName && (
                        (!isFrom && !isTo) ||
                        (isFrom && isTo && testFrom(fromName) && testTo(toName)) ||
                        (isFrom && !isTo && testFrom(fromName)) ||
                        (!isFrom && isTo && testTo(toName)))) {
                        this.stateData.setMix(fromName, toName, mix);
                    }
                }
            }
        }
    }

    public getDuration(...names: string[]): number {
        return this.spineData.animations
            .filter((it) => names.indexOf(it.name) > 1)
            .reduce((sum, it) => sum + it.duration, 0);
    }

    public destroy(): void {
        if (this._onAnimationComplete) {
            this._onAnimationComplete.remove();
        }
        if (this._onAnimationEvent) {
            this._onAnimationEvent.remove();
        }
        super.destroy({children: true});
    }

    public debugAnimations() {
        this.interactive = true;
        this.on("click", () => {
            const track = this.getCurrentTrack();
            let index = track ? this.spineData.animations.reduce((a, t, i) => track.animation.name === t.name ? i : a, -1) : -1;
            index++;
            if (index >= this.spineData.animations.length) {
                index = 0;
            }
            this.animate(this.spineData.animations[index].name, -1);
            console.log("[Animation] ", this.spineData.animations[index].name);
        });
    }

    private addSpineListeners(): void {
        if (!this._onAnimationComplete) {
            this._onAnimationComplete = Signal<(track: PIXI.spine.core.TrackEntry) => void>();
            this._onAnimationEvent = Signal<(track: PIXI.spine.core.TrackEntry, event: PIXI.spine.core.Event) => void>();
            this.state.addListener({
                complete: this._onAnimationComplete,
                event: this._onAnimationEvent,
            });
        }
    }
}

export class SpineTrack implements ISpineTrack {

    private _currentLoop?: PIXI.spine.core.TrackEntry;
    private readonly spine: Spine;
    private readonly index: number;

    constructor(spine: Spine, index: number) {
        this.spine = spine;
        this.index = index;
    }

    public animate(animationName: string, duration?: number | Callback, cb?: Callback): PIXI.spine.core.TrackEntry {

        let track: PIXI.spine.core.TrackEntry;
        if (typeof duration === "number" && duration < 0) {

            if (!this._currentLoop) {
                this._currentLoop = track = this.spine.state.setAnimation(this.index, animationName, true);
            } else if (this._currentLoop.animation.name !== animationName) {
                this._currentLoop = track = this.spine.state.setAnimation(this.index, animationName, true);
            } else {
                track = this._currentLoop;
            }

        } else if (typeof duration === "number") {

            this._currentLoop = undefined;
            track = this.spine.state.setAnimation(this.index, animationName, false);
            track.timeScale = track.animation.duration / duration;

            if (typeof cb === "function") {
                this.callOnComplete(animationName, cb);
            }

        } else if (typeof duration === "function") {

            this._currentLoop = undefined;
            track = this.spine.state.setAnimation(this.index, animationName, false);
            this.callOnComplete(animationName, duration);

        } else {

            this._currentLoop = undefined;
            track = this.spine.state.setAnimation(this.index, animationName, false);
        }

        this.spine.update(0);
        return track;
    }

    public getCurrentTrack(): PIXI.spine.core.TrackEntry {
        return this.spine.state.getCurrent(this.index);
    }

    public stopAnimation(mix: number = 0): void {
        this._currentLoop = undefined;
        this.spine.state.setEmptyAnimation(this.index, mix);
    }

    private callOnComplete(animationName: string, listener: Callback) {
        const onComplete = (track: PIXI.spine.core.TrackEntry) => {
            const current = this.spine.state.getCurrent(this.index);
            if (track.trackIndex === this.index &&
                track.animation.name === animationName &&
                current !== null && current.animation.name === animationName) {
                this.spine.onAnimationComplete.remove(onComplete);
                listener();
            }
        };
        this.spine.onAnimationComplete.add(onComplete);
    }
}
